// RadicalSymbolSwapDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { getCardTextColor, getLetters } from "./RadicalHelpers";

const normalizeCollectOrder = (order, letters) => {
    const used = [];

    (Array.isArray(order) ? order : []).forEach((letter) => {
        if (letters.includes(letter) && !used.includes(letter)) {
            used.push(letter);
        }
    });

    return [...used, ...letters.filter((letter) => !used.includes(letter))];
};

const MiniResultCard = ({ card, index }) => {
    const changed = card.code !== card.dealingCode;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.25,
            }}
        >
            <Paper
                variant="outlined"
                title={`${card.value} · ${card.code}${
                    changed ? ` → ${card.dealingCode}` : ""
                }`}
                sx={{
                    width: 82,
                    minWidth: 82,
                    height: 48,
                    px: 0.75,
                    py: 0.4,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    borderRadius: 1.2,
                    overflow: "hidden",
                    bgcolor: changed ? "action.hover" : "background.paper",
                    borderColor: changed ? "primary.main" : "divider",
                    userSelect: "none",
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 700,
                        lineHeight: 1.1,
                        fontSize: 12,
                        color: getCardTextColor(card.value),
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {card.value}
                </Typography>

                <Typography
                    variant="caption"
                    sx={{
                        lineHeight: 1,
                        fontSize: 10,
                        fontFamily: "monospace",
                        color: "text.secondary",
                    }}
                >
                    {changed ? `${card.code} → ${card.dealingCode}` : card.code}
                </Typography>
            </Paper>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 9 }}
            >
                {index + 1}
            </Typography>
        </Box>
    );
};

const RelationEditor = ({
    title,
    letters,
    relations,
    currentPictograms,
    onChange,
    onInvert,
    onReset,
}) => {
    const selectedCounts = letters.reduce((acc, letter) => {
        const selected = relations[letter] || letter;

        acc[selected] = (acc[selected] || 0) + 1;
        return acc;
    }, {});

    const symbolText = (letter) =>
        `${currentPictograms[letter] || letter} ${letter}`;

    return (
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
            >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onInvert}
                        sx={{ textTransform: "none" }}
                    >
                        Invertir
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onReset}
                        sx={{ textTransform: "none" }}
                    >
                        Reset
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={1}>
                {letters.map((letter) => (
                    <Grid item xs="auto" key={letter}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 1.5,
                                minWidth: 230,
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Box sx={{ minWidth: 54 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {currentPictograms[letter] || letter}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {letter}
                                    </Typography>
                                </Box>

                                <Typography color="text.secondary">
                                    →
                                </Typography>

                                <FormControl size="small" fullWidth>
                                    <InputLabel
                                        id={`symbol-relation-${title}-${letter}`}
                                    >
                                        Usar
                                    </InputLabel>

                                    <Select
                                        labelId={`symbol-relation-${title}-${letter}`}
                                        label="Usar"
                                        value={relations[letter] || letter}
                                        onChange={(event) =>
                                            onChange(letter, event.target.value)
                                        }
                                        renderValue={symbolText}
                                    >
                                        {letters.map((option) => {
                                            const usedByOther = letters.some(
                                                (source) =>
                                                    source !== letter &&
                                                    (relations[source] ||
                                                        source) === option,
                                            );

                                            return (
                                                <MenuItem
                                                    key={`${letter}-${option}`}
                                                    value={option}
                                                    sx={{
                                                        bgcolor: usedByOther
                                                            ? "action.hover"
                                                            : "background.paper",
                                                        color: usedByOther
                                                            ? "text.secondary"
                                                            : "text.primary",
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        sx={{ width: "100%" }}
                                                    >
                                                        <span>
                                                            {symbolText(option)}
                                                        </span>

                                                        {usedByOther && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {selectedCounts[
                                                                    option
                                                                ] === 1
                                                                    ? "usada"
                                                                    : `x${selectedCounts[option]}`}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
};

const CollectOrderEditor = ({
    title,
    letters,
    collectOrder,
    currentPictograms,
    onChange,
    onInvert,
    onReset,
}) => {
    const safeCollectOrder = normalizeCollectOrder(collectOrder, letters);
    const symbolText = (letter) =>
        `${currentPictograms[letter] || letter} ${letter}`;

    return (
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
            >
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {title}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        Orden actual:{" "}
                        {safeCollectOrder
                            .map(
                                (letter) => currentPictograms[letter] || letter,
                            )
                            .join(" ")}{" "}
                        · {safeCollectOrder.join("")}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onInvert}
                        sx={{ textTransform: "none" }}
                    >
                        Invertir
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={onReset}
                        sx={{ textTransform: "none" }}
                    >
                        Reset
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={1}>
                {safeCollectOrder.map((letter, index) => (
                    <Grid item xs="auto" key={`collect-${title}-${index}`}>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1,
                                borderRadius: 1.5,
                                minWidth: 160,
                            }}
                        >
                            <FormControl size="small" fullWidth>
                                <InputLabel
                                    id={`collect-order-${title}-${index}`}
                                >
                                    {index + 1}º
                                </InputLabel>

                                <Select
                                    labelId={`collect-order-${title}-${index}`}
                                    label={`${index + 1}º`}
                                    value={letter}
                                    onChange={(event) => {
                                        const nextLetter = event.target.value;
                                        const previousIndex =
                                            safeCollectOrder.indexOf(
                                                nextLetter,
                                            );
                                        const next = [...safeCollectOrder];

                                        next[previousIndex] = letter;
                                        next[index] = nextLetter;

                                        onChange(next);
                                    }}
                                    renderValue={symbolText}
                                >
                                    {letters.map((option) => {
                                        const optionIndex =
                                            safeCollectOrder.indexOf(option);
                                        const usedInOtherPosition =
                                            optionIndex !== index;

                                        return (
                                            <MenuItem
                                                key={`collect-${title}-${index}-${option}`}
                                                value={option}
                                                sx={{
                                                    bgcolor: usedInOtherPosition
                                                        ? "action.hover"
                                                        : "background.paper",
                                                    color: usedInOtherPosition
                                                        ? "text.secondary"
                                                        : "text.primary",
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    sx={{ width: "100%" }}
                                                >
                                                    <span>
                                                        {symbolText(option)}
                                                    </span>

                                                    {usedInOtherPosition && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            {optionIndex + 1}º
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
};

const RadicalSymbolSwapDialog = ({
    open,
    onClose,
    rows = [],
    cardsByCode = {},
    safePackets,
    safeDeals,
    currentPictograms = {},
    deckOrientation = "cara",
    selectedPresetName,
    description,
    handlingPresets = [],
    onHandlingPresetsChange,
}) => {
    const dealsCount = Math.max(1, parseInt(safeDeals, 10) || 1);
    const letters = useMemo(() => getLetters(safePackets), [safePackets]);

    const identityRelations = useMemo(
        () =>
            letters.reduce((acc, letter) => {
                acc[letter] = letter;
                return acc;
            }, {}),
        [letters],
    );

    const defaultRelationsByDeal = useMemo(
        () =>
            Array.from({ length: dealsCount }, () => ({
                ...identityRelations,
            })),
        [dealsCount, identityRelations],
    );

    const defaultCollectOrdersByDeal = useMemo(
        () => Array.from({ length: dealsCount }, () => [...letters]),
        [dealsCount, letters],
    );
    const defaultPacketFlipsByDeal = useMemo(
        () => Array.from({ length: dealsCount }, () => ({})),
        [dealsCount],
    );

    const [useSameRelations, setUseSameRelations] = useState(true);
    const [useSameCollectOrder, setUseSameCollectOrder] = useState(true);
    const [relationsByDeal, setRelationsByDeal] = useState(
        defaultRelationsByDeal,
    );
    const [collectOrdersByDeal, setCollectOrdersByDeal] = useState(
        defaultCollectOrdersByDeal,
    );
    const [packetFlipsByDeal, setPacketFlipsByDeal] = useState(
        defaultPacketFlipsByDeal,
    );

    const safeHandlingPresets = useMemo(
        () => (Array.isArray(handlingPresets) ? handlingPresets : []),
        [handlingPresets],
    );

    useEffect(() => {
        if (open) {
            setUseSameRelations(true);
            setUseSameCollectOrder(true);
            setRelationsByDeal(defaultRelationsByDeal);
            setCollectOrdersByDeal(defaultCollectOrdersByDeal);
            setPacketFlipsByDeal(defaultPacketFlipsByDeal);
        }
    }, [
        open,
        defaultRelationsByDeal,
        defaultCollectOrdersByDeal,
        defaultPacketFlipsByDeal,
    ]);

    const safeRelationsByDeal = useMemo(() => {
        return Array.from({ length: dealsCount }, (_, index) => ({
            ...identityRelations,
            ...(relationsByDeal[index] || relationsByDeal[0] || {}),
        }));
    }, [dealsCount, identityRelations, relationsByDeal]);

    const safeCollectOrdersByDeal = useMemo(() => {
        return Array.from({ length: dealsCount }, (_, index) =>
            normalizeCollectOrder(
                collectOrdersByDeal[index] || collectOrdersByDeal[0] || letters,
                letters,
            ),
        );
    }, [dealsCount, collectOrdersByDeal, letters]);

    const safePacketFlipsByDeal = useMemo(() => {
        return Array.from({ length: dealsCount }, (_, index) => ({
            ...(packetFlipsByDeal[index] || {}),
        }));
    }, [dealsCount, packetFlipsByDeal]);

    const togglePacketFlip = (dealIndex, letter) => {
        setPacketFlipsByDeal((prev) => {
            const next = Array.from({ length: dealsCount }, (_, index) => ({
                ...(prev[index] || {}),
            }));

            next[dealIndex][letter] = !next[dealIndex][letter];

            return next;
        });
    };

    const relationEditors = useSameRelations ? [0] : safeRelationsByDeal.keys();
    const collectOrderEditors = useSameCollectOrder
        ? [0]
        : safeCollectOrdersByDeal.keys();

    const updateRelation = (dealIndex, sourceLetter, targetLetter) => {
        setRelationsByDeal((prev) => {
            const next = Array.from({ length: dealsCount }, (_, index) => ({
                ...identityRelations,
                ...(prev[index] || prev[0] || {}),
            }));

            next[dealIndex] = {
                ...next[dealIndex],
                [sourceLetter]: targetLetter,
            };

            return next;
        });
    };

    const resetRelations = (dealIndex) => {
        setRelationsByDeal((prev) => {
            const next = Array.from({ length: dealsCount }, (_, index) => ({
                ...identityRelations,
                ...(prev[index] || prev[0] || {}),
            }));

            next[dealIndex] = { ...identityRelations };

            return next;
        });
    };
    const invertRelations = (dealIndex) => {
        setRelationsByDeal((prev) => {
            const next = Array.from({ length: dealsCount }, (_, index) => ({
                ...identityRelations,
                ...(prev[index] || prev[0] || {}),
            }));

            const current = next[dealIndex];

            next[dealIndex] = letters.reduce((acc, letter, index) => {
                const oppositeLetter = letters[letters.length - 1 - index];

                acc[letter] = current[oppositeLetter] || oppositeLetter;
                return acc;
            }, {});

            return next;
        });
    };

    const updateCollectOrder = (dealIndex, nextOrder) => {
        setCollectOrdersByDeal((prev) => {
            const next = Array.from({ length: dealsCount }, (_, index) =>
                normalizeCollectOrder(
                    prev[index] || prev[0] || letters,
                    letters,
                ),
            );

            next[dealIndex] = normalizeCollectOrder(nextOrder, letters);

            return next;
        });
    };

    const resetCollectOrder = (dealIndex) => {
        setCollectOrdersByDeal((prev) => {
            const next = Array.from({ length: dealsCount }, (_, index) =>
                normalizeCollectOrder(
                    prev[index] || prev[0] || letters,
                    letters,
                ),
            );

            next[dealIndex] = [...letters];

            return next;
        });
    };
    const invertCollectOrder = (dealIndex) => {
        setCollectOrdersByDeal((prev) => {
            const next = Array.from({ length: dealsCount }, (_, index) =>
                normalizeCollectOrder(
                    prev[index] || prev[0] || letters,
                    letters,
                ),
            );

            next[dealIndex] = [...next[dealIndex]].reverse();

            return next;
        });
    };

    const handleSameRelationsChange = (event) => {
        const checked = event.target.checked;

        setUseSameRelations(checked);

        if (!checked) {
            setRelationsByDeal((prev) =>
                Array.from({ length: dealsCount }, (_, index) => ({
                    ...identityRelations,
                    ...(prev[index] || prev[0] || {}),
                })),
            );
        }
    };

    const handleSameCollectOrderChange = (event) => {
        const checked = event.target.checked;

        setUseSameCollectOrder(checked);

        if (!checked) {
            setCollectOrdersByDeal((prev) =>
                Array.from({ length: dealsCount }, (_, index) =>
                    normalizeCollectOrder(
                        prev[index] || prev[0] || letters,
                        letters,
                    ),
                ),
            );
        }
    };

    const getHandlingPresetDeals = () => {
        return Array.from({ length: dealsCount }, (_, index) => {
            const collectOrder = useSameCollectOrder
                ? safeCollectOrdersByDeal[0]
                : safeCollectOrdersByDeal[index];

            const packetFlips = safePacketFlipsByDeal[index] || {};

            return {
                collectOrder: normalizeCollectOrder(collectOrder, letters).join(
                    "",
                ),
                flipPackets: letters
                    .filter((letter) => packetFlips[letter])
                    .join(""),
            };
        });
    };

    const applyHandlingPreset = (handlingPreset) => {
        const presetDeals = Array.isArray(handlingPreset.deals)
            ? handlingPreset.deals
            : [];

        const nextCollectOrdersByDeal = Array.from(
            { length: dealsCount },
            (_, index) =>
                normalizeCollectOrder(
                    String(presetDeals[index]?.collectOrder || "").split(""),
                    letters,
                ),
        );

        const nextPacketFlipsByDeal = Array.from(
            { length: dealsCount },
            (_, index) => {
                const flipPackets = String(
                    presetDeals[index]?.flipPackets || "",
                );

                return letters.reduce((acc, letter) => {
                    if (flipPackets.includes(letter)) {
                        acc[letter] = true;
                    }

                    return acc;
                }, {});
            },
        );

        setUseSameCollectOrder(
            nextCollectOrdersByDeal.every(
                (order) =>
                    order.join("") === nextCollectOrdersByDeal[0].join(""),
            ),
        );
        setCollectOrdersByDeal(nextCollectOrdersByDeal);
        setPacketFlipsByDeal(nextPacketFlipsByDeal);
    };

    const handleSaveHandlingPreset = () => {
        if (!onHandlingPresetsChange) {
            return;
        }

        const name = window.prompt(
            "Nombre de la versión de manejo",
            selectedPresetName || "Versión",
        );

        if (!name || !name.trim()) {
            return;
        }

        const trimmedName = name.trim();
        const id =
            trimmedName
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "") || `handling-${Date.now()}`;

        const existingPreset = safeHandlingPresets.find(
            (preset) =>
                preset.id === id ||
                String(preset.name || "").toLowerCase() ===
                    trimmedName.toLowerCase(),
        );

        const nextPreset = {
            id: existingPreset?.id || id,
            name: trimmedName,
            deals: getHandlingPresetDeals(),
        };

        onHandlingPresetsChange([
            nextPreset,
            ...safeHandlingPresets.filter(
                (preset) => preset.id !== nextPreset.id,
            ),
        ]);
    };

    const simulation = useMemo(() => {
        const initialDeck = rows
            .map((row) => {
                const value = cardsByCode[row.finalCode] || "";
                const dealingCode = String(row.finalCode || "")
                    .split("")
                    .map((letter, index) => {
                        const dealRelations = useSameRelations
                            ? safeRelationsByDeal[0]
                            : safeRelationsByDeal[index];

                        return dealRelations?.[letter] || letter;
                    })
                    .join("");

                return {
                    id: row.finalCode,
                    code: row.finalCode,
                    value,
                    dealingCode,
                };
            })
            .filter((card) => String(card.value).trim() !== "")
            .sort((a, b) => a.code.localeCompare(b.code));

        const steps = [];
        let currentDeck = initialDeck;

        for (let passIndex = 0; passIndex < dealsCount; passIndex += 1) {
            const dealRelations = useSameRelations
                ? safeRelationsByDeal[0]
                : safeRelationsByDeal[passIndex];

            const collectOrder = useSameCollectOrder
                ? safeCollectOrdersByDeal[0]
                : safeCollectOrdersByDeal[passIndex];

            const packetFlips = safePacketFlipsByDeal[passIndex] || {};

            const piles = letters.reduce((acc, letter) => {
                acc[letter] = [];
                return acc;
            }, {});

            currentDeck.forEach((card) => {
                const sourceLetter = card.code?.[passIndex];
                const pileLetter =
                    dealRelations?.[sourceLetter] || sourceLetter;

                if (piles[pileLetter]) {
                    piles[pileLetter].push(card);
                }
            });

            const collectedDeck = [];

            collectOrder.forEach((letter) => {
                const pile = [...piles[letter]];

                collectedDeck.push(
                    ...(packetFlips[letter] ? pile : [...pile].reverse()),
                );
            });

            steps.push({
                passNumber: passIndex + 1,
                collectOrder,
                piles,
                collectedDeck,
            });

            currentDeck = collectedDeck;
        }

        return {
            initialDeck,
            steps,
            finalDeck:
                deckOrientation === "dorso"
                    ? currentDeck
                    : [...currentDeck].reverse(),
        };
    }, [
        rows,
        cardsByCode,
        dealsCount,
        letters,
        useSameRelations,
        safeRelationsByDeal,
        useSameCollectOrder,
        safeCollectOrdersByDeal,
        deckOrientation,
        safePacketFlipsByDeal,
    ]);

    const changedRelationsCount = useMemo(() => {
        const relationsToCheck = useSameRelations
            ? [safeRelationsByDeal[0]]
            : safeRelationsByDeal;

        return relationsToCheck.reduce((sum, relations) => {
            return (
                sum +
                letters.filter(
                    (letter) => (relations[letter] || letter) !== letter,
                ).length
            );
        }, 0);
    }, [useSameRelations, safeRelationsByDeal, letters]);

    const changedCollectOrdersCount = useMemo(() => {
        const defaultOrder = letters.join("");
        const ordersToCheck = useSameCollectOrder
            ? [safeCollectOrdersByDeal[0]]
            : safeCollectOrdersByDeal;

        return ordersToCheck.filter((order) => order.join("") !== defaultOrder)
            .length;
    }, [useSameCollectOrder, safeCollectOrdersByDeal, letters]);

    const changedCardsCount = simulation.initialDeck.filter(
        (card) => card.code !== card.dealingCode,
    ).length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Conectar símbolos de la receta</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                        }}
                    >
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                            sx={{ mb: safeHandlingPresets.length > 0 ? 1 : 0 }}
                        >
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    Versiones de manejo
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Guarda y carga órdenes de recogida e
                                    inversión de paquetes.
                                </Typography>
                            </Box>

                            <Button
                                size="small"
                                variant="contained"
                                disabled={!onHandlingPresetsChange}
                                onClick={handleSaveHandlingPreset}
                                sx={{ textTransform: "none" }}
                            >
                                Guardar versión
                            </Button>
                        </Stack>

                        {safeHandlingPresets.length > 0 && (
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ flexWrap: "wrap" }}
                            >
                                {safeHandlingPresets.map((handlingPreset) => (
                                    <Button
                                        key={handlingPreset.id}
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                            applyHandlingPreset(handlingPreset)
                                        }
                                        sx={{ textTransform: "none" }}
                                    >
                                        {handlingPreset.name}
                                    </Button>
                                ))}
                            </Stack>
                        )}
                    </Paper>
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 1.5,
                            borderRadius: 1.5,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {selectedPresetName || "Custom"}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                whiteSpace: "pre-line",
                                mt: 0.5,
                            }}
                        >
                            {description ||
                                "Simula qué pasaría si una dada marcada con un símbolo se hiciera como otro símbolo."}
                        </Typography>
                    </Paper>
                    <Box>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                        >
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    Relaciones
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Cuando la receta pida un símbolo, puedes
                                    hacer que se comporte como otro. Se puede
                                    repetir destino.
                                </Typography>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useSameRelations}
                                        onChange={handleSameRelationsChange}
                                    />
                                }
                                label="Usar lo mismo en todas las dadas"
                            />
                        </Stack>

                        <Stack spacing={1}>
                            {[...relationEditors].map((dealIndex) => (
                                <RelationEditor
                                    key={`relations-${dealIndex}`}
                                    title={
                                        useSameRelations
                                            ? "Todas las dadas"
                                            : `Dada ${dealIndex + 1}`
                                    }
                                    letters={letters}
                                    relations={safeRelationsByDeal[dealIndex]}
                                    currentPictograms={currentPictograms}
                                    onChange={(sourceLetter, targetLetter) =>
                                        updateRelation(
                                            dealIndex,
                                            sourceLetter,
                                            targetLetter,
                                        )
                                    }
                                    onInvert={() => invertRelations(dealIndex)}
                                    onReset={() => resetRelations(dealIndex)}
                                />
                            ))}
                        </Stack>
                    </Box>
                    <Box>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                        >
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    Orden de recogida de paquetes
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    De normal se recoge en orden{" "}
                                    {letters.join("")}. Al cambiar una posición,
                                    se intercambia con la otra para no perder
                                    paquetes.
                                </Typography>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useSameCollectOrder}
                                        onChange={handleSameCollectOrderChange}
                                    />
                                }
                                label="Usar lo mismo en todas las dadas"
                            />
                        </Stack>

                        <Stack spacing={1}>
                            {[...collectOrderEditors].map((dealIndex) => (
                                <CollectOrderEditor
                                    key={`collect-${dealIndex}`}
                                    title={
                                        useSameCollectOrder
                                            ? "Todas las dadas"
                                            : `Dada ${dealIndex + 1}`
                                    }
                                    letters={letters}
                                    collectOrder={
                                        safeCollectOrdersByDeal[dealIndex]
                                    }
                                    currentPictograms={currentPictograms}
                                    onChange={(nextOrder) =>
                                        updateCollectOrder(dealIndex, nextOrder)
                                    }
                                    onInvert={() =>
                                        invertCollectOrder(dealIndex)
                                    }
                                    onReset={() => resetCollectOrder(dealIndex)}
                                />
                            ))}
                        </Stack>
                    </Box>
                    <Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                            >
                                Invertir paquetes antes de montar
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Marca los paquetes que se giran antes de
                                montarlos en cada dada.
                            </Typography>
                        </Box>

                        <Stack spacing={1}>
                            {safePacketFlipsByDeal.map(
                                (packetFlips, dealIndex) => (
                                    <Paper
                                        key={`packet-flips-${dealIndex}`}
                                        variant="outlined"
                                        sx={{ p: 1.25, borderRadius: 1.5 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 700, mb: 0.5 }}
                                        >
                                            Dada {dealIndex + 1}
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ flexWrap: "wrap" }}
                                        >
                                            {letters.map((letter) => (
                                                <FormControlLabel
                                                    key={`packet-flip-${dealIndex}-${letter}`}
                                                    control={
                                                        <Checkbox
                                                            checked={Boolean(
                                                                packetFlips[
                                                                    letter
                                                                ],
                                                            )}
                                                            onChange={() =>
                                                                togglePacketFlip(
                                                                    dealIndex,
                                                                    letter,
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label={`${currentPictograms[letter] || letter} ${letter}`}
                                                />
                                            ))}
                                        </Stack>
                                    </Paper>
                                ),
                            )}
                        </Stack>
                    </Box>
                    <Divider />
                    <Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                            >
                                Resultado simulado
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {changedRelationsCount === 0
                                    ? "No hay cambios en las relaciones."
                                    : `${changedRelationsCount} relación${
                                          changedRelationsCount === 1
                                              ? ""
                                              : "es"
                                      } cambiada${
                                          changedRelationsCount === 1 ? "" : "s"
                                      }.`}{" "}
                                {changedCollectOrdersCount === 0
                                    ? "La recogida está en orden normal."
                                    : `${changedCollectOrdersCount} orden${
                                          changedCollectOrdersCount === 1
                                              ? ""
                                              : "es"
                                      } de recogida cambiado${
                                          changedCollectOrdersCount === 1
                                              ? ""
                                              : "s"
                                      }.`}{" "}
                                {changedCardsCount} carta
                                {changedCardsCount === 1 ? "" : "s"} afectada
                                {changedCardsCount === 1 ? "" : "s"}.
                            </Typography>
                        </Box>

                        {simulation.finalDeck.length === 0 ? (
                            <Alert severity="info">
                                No hay cartas asignadas para simular.
                            </Alert>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                    alignItems: "flex-start",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        alignSelf: "center",
                                        mr: 0.5,
                                    }}
                                >
                                    Top{" "}
                                    {deckOrientation === "dorso"
                                        ? "(dorso)"
                                        : "(cara)"}
                                </Typography>

                                {simulation.finalDeck.map((card, index) => (
                                    <MiniResultCard
                                        key={`${card.id}-${index}`}
                                        card={card}
                                        index={index}
                                    />
                                ))}

                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        alignSelf: "center",
                                        ml: 0.5,
                                    }}
                                >
                                    Bottom{" "}
                                    {deckOrientation === "dorso"
                                        ? "(dorso)"
                                        : "(cara)"}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RadicalSymbolSwapDialog;
